import React from 'react';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: '20mm 15mm',
    fontFamily: 'Helvetica',
    fontSize: 11,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: 12,
    textAlign: 'center',
  },
  divider: {
    height: 2,
    backgroundColor: '#3b82f6',
    marginBottom: 16,
  },
  intro: {
    fontSize: 11,
    lineHeight: 1.6,
    color: '#374151',
    marginBottom: 16,
    textAlign: 'justify',
  },
  phaseBlock: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  phaseName: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 6,
  },
  phaseDesc: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#4b5563',
    marginBottom: 6,
    textAlign: 'justify',
  },
  phaseTechniques: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 4,
  },
  bulletList: {
    marginLeft: 12,
    marginBottom: 6,
  },
  bulletItem: {
    fontSize: 9,
    lineHeight: 1.5,
    color: '#6b7280',
    marginBottom: 3,
    textAlign: 'justify',
  },
  phaseSummary: {
    fontSize: 9,
    lineHeight: 1.5,
    color: '#6b7280',
    marginTop: 6,
    fontStyle: 'italic',
    textAlign: 'justify',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

const phases = [
  {
    name: 'Pre-Engagement & Scoping',
    description: `We establish the scope of the source code review, identifying critical components, programming languages, frameworks, and security requirements to ensure a focused and effective assessment.`,
    techniques: [
      `Review project documentation, architecture diagrams, and technology stack to understand the application's structure, dependencies, and data flows.`,
      `Identify high-risk components such as authentication modules, payment processing, API endpoints, and data handling functions that require thorough analysis.`,
      `Establish access to source code repositories (e.g., GitHub, GitLab, Bitbucket) and development environments, ensuring proper permissions and version control.`,
      `Define testing boundaries, including in-scope files, modules, and third-party libraries, while excluding out-of-scope components to optimize review efficiency.`,
      `Conduct stakeholder interviews to understand business logic, security concerns, and compliance requirements (e.g., PCI-DSS, GDPR, HIPAA).`,
    ],
    summary: `This phase ensures alignment with the client's objectives and provides a clear roadmap for the source code security assessment.`,
  },
  {
    name: 'Static Code Analysis',
    description: `We perform automated and manual static analysis to identify security vulnerabilities, coding flaws, and adherence to secure coding standards without executing the code.`,
    techniques: [
      `Use automated static analysis tools (e.g., SonarQube, Checkmarx, Fortify, Semgrep) to scan for common vulnerabilities such as SQL injection, XSS, hardcoded secrets, and insecure configurations.`,
      `Analyze code for OWASP Top 10 vulnerabilities, including injection flaws, broken authentication, sensitive data exposure, and security misconfigurations.`,
      `Review authentication and authorization mechanisms to identify weaknesses such as weak password policies, insecure session management, and privilege escalation risks.`,
      `Examine input validation and sanitization routines to detect potential injection vulnerabilities (SQL, NoSQL, LDAP, OS command injection).`,
      `Identify hardcoded credentials, API keys, tokens, and sensitive information in source code, configuration files, and environment variables.`,
      `Assess cryptographic implementations for weak algorithms (e.g., MD5, SHA1), improper key management, and insecure random number generation.`,
      `Review error handling and logging mechanisms to ensure sensitive information is not exposed in error messages or logs.`,
    ],
    summary: `This phase leverages both automated tools and manual expertise to uncover security flaws that could be exploited by attackers.`,
  },
  {
    name: 'Manual Code Review',
    description: `We conduct in-depth manual code review to identify complex logic flaws, business logic vulnerabilities, and security issues that automated tools may miss.`,
    techniques: [
      `Trace data flows from user inputs to database queries, API calls, and file operations to identify injection points and data leakage risks.`,
      `Analyze business logic for flaws such as race conditions, improper state management, and authorization bypass vulnerabilities.`,
      `Review API implementations for security issues including missing authentication, excessive data exposure, rate limiting, and improper error handling.`,
      `Examine file upload and download functionalities for path traversal, unrestricted file uploads, and insufficient file type validation.`,
      `Assess session management for vulnerabilities such as session fixation, predictable session IDs, and improper session timeout configurations.`,
      `Review third-party library usage and dependencies for known vulnerabilities using tools like npm audit, pip-audit, or OWASP Dependency-Check.`,
      `Analyze access control mechanisms to ensure proper role-based access control (RBAC) and prevent horizontal/vertical privilege escalation.`,
    ],
    summary: `This phase focuses on understanding the application's logic and identifying vulnerabilities that require human expertise and contextual understanding.`,
  },
  // {
  //   name: 'Dependency & Supply Chain Analysis',
  //   description: `We assess third-party libraries, frameworks, and dependencies for known vulnerabilities and supply chain risks that could compromise application security.`,
  //   techniques: [
  //     `Scan dependencies using tools like Snyk, npm audit, pip-audit, or OWASP Dependency-Check to identify outdated or vulnerable libraries.`,
  //     `Review package.json, requirements.txt, pom.xml, or other dependency manifests for insecure or unmaintained packages.`,
  //     `Analyze transitive dependencies (dependencies of dependencies) for hidden vulnerabilities that may not be immediately visible.`,
  //     `Check for typosquatting attacks, malicious packages, and supply chain compromises in public repositories (e.g., npm, PyPI, Maven Central).`,
  //     `Assess license compliance and identify open-source components with restrictive or incompatible licenses.`,
  //   ],
  //   summary: `This phase ensures that third-party components do not introduce security risks or compliance issues into the application.`,
  // },
  // {
  //   name: 'Configuration & Infrastructure Review',
  //   description: `We examine configuration files, environment variables, and infrastructure-as-code (IaC) for security misconfigurations and hardening opportunities.`,
  //   techniques: [
  //     `Review configuration files (e.g., .env, config.yaml, web.config) for hardcoded secrets, insecure defaults, and exposed sensitive information.`,
  //     `Analyze Docker, Kubernetes, and cloud infrastructure configurations (e.g., AWS CloudFormation, Terraform) for security misconfigurations.`,
  //     `Assess database connection strings, API endpoints, and service URLs for insecure protocols (e.g., HTTP instead of HTTPS).`,
  //     `Review CORS policies, CSP headers, and security headers (e.g., X-Frame-Options, Strict-Transport-Security) for proper implementation.`,
  //     `Examine CI/CD pipeline configurations for security weaknesses such as exposed credentials, insecure build processes, and insufficient access controls.`,
  //   ],
  //   summary: `This phase identifies configuration-level vulnerabilities that could be exploited to compromise the application or its infrastructure.`,
  // },
  {
    name: 'Reporting & Remediation Guidance',
    description: `We document all identified vulnerabilities with detailed descriptions, risk ratings, proof-of-concept code, and actionable remediation recommendations.`,
    techniques: [
      `Classify vulnerabilities by severity (Critical, High, Medium, Low) based on CVSS scores and business impact.`,
      `Provide detailed descriptions of each vulnerability, including affected code locations, exploitation scenarios, and potential impact.`,
      `Include proof-of-concept code snippets or exploitation steps to demonstrate the vulnerability's exploitability.`,
      `Offer specific remediation guidance with secure code examples, best practices, and references to secure coding standards (e.g., OWASP, CWE).`,
      `Prioritize findings based on exploitability, business impact, and ease of remediation to help development teams address critical issues first.`,
      `Conduct a remediation review or retest after fixes are implemented to verify that vulnerabilities have been properly addressed.`,
    ],
    summary: `This phase ensures that findings are actionable, stakeholders are informed, and the application's security is improved through effective remediation and secure coding practices.`,
  },
];

interface SourceCodeMethodologyPageProps {
  documentNumber?: string;
  registerSectionPage?: (section: string, page: number) => void;
  sectionId?: string;
}

const SourceCodeMethodologyPage: React.FC<SourceCodeMethodologyPageProps> = ({ documentNumber, registerSectionPage, sectionId }) => (
  <Page size="A4" id={sectionId} style={styles.page}>
    {/* Register section page for TOC */}
    {registerSectionPage && (
      <Text style={{ display: 'none', fontSize: 1 }} render={({ pageNumber }) => {
        registerSectionPage('SourceCodeMethodology', pageNumber);
        return '';
      }} />
    )}
    <Text style={styles.sectionTitle}>Source Code Security Review Methodology</Text>
    <View style={styles.divider} />
    <Text style={styles.intro}>
      Our source code security review methodology identifies vulnerabilities in application source code through a comprehensive, multi-layered approach combining automated analysis and manual expert review. This methodology ensures thorough evaluation of code security, adherence to secure coding standards, and identification of both common and complex vulnerabilities.
    </Text>
    {phases.map((phase) => (
      <View key={phase.name} style={styles.phaseBlock}>
        <Text style={styles.phaseName}>{phase.name}</Text>
        <Text style={styles.phaseDesc}><Text style={{ fontWeight: 'bold' }}>Description:</Text> {phase.description}</Text>
        <Text style={styles.phaseTechniques}>Techniques:</Text>
        <View style={styles.bulletList}>
          {phase.techniques.map((tech, i) => (
            <Text key={i} style={styles.bulletItem}>• {tech}</Text>
          ))}
        </View>
        {phase.summary && <Text style={styles.phaseSummary}>{phase.summary}</Text>}
      </View>
    ))}
    <View style={styles.footer} fixed>
      <Text style={styles.footerLeft}>{documentNumber || 'Document Number'}</Text>
      <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
    </View>
  </Page>
);

export default SourceCodeMethodologyPage;

